import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  MapPin, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Lock,
  FileText,
  Download,
  Wallet,
  Users,
  Calendar,
  Target,
  Shield,
  Clock,
  Building2,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  ExternalLink,
  Info,
  Eye
} from 'lucide-react';

// ==================== HELPER COMPONENTS ====================

// Skeleton loader for metrics
const MetricSkeleton = () => (
  <div className="p-4 rounded-sm border border-border bg-muted/20 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="space-y-2 flex-1">
        <div className="h-3 w-20 bg-muted rounded" />
        <div className="h-6 w-28 bg-muted rounded" />
        <div className="h-3 w-16 bg-muted rounded" />
      </div>
      <div className="p-2 rounded-sm bg-muted w-8 h-8" />
    </div>
  </div>
);

// Loading skeleton for entire page
const PageSkeleton = () => (
  <div className="space-y-8 max-w-6xl mx-auto animate-pulse">
    <div className="h-4 w-40 bg-muted rounded" />
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="h-6 w-32 bg-muted rounded" />
        <div className="h-6 w-24 bg-muted rounded" />
      </div>
      <div className="h-10 w-96 bg-muted rounded" />
      <div className="h-4 w-64 bg-muted rounded" />
    </div>
    <Card className="card-dashboard">
      <CardContent className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <MetricSkeleton key={i} />)}
        </div>
      </CardContent>
    </Card>
  </div>
);

const MetricCard = ({ label, value, sublabel, icon: Icon, highlight = false }) => (
  <div className={`p-3 sm:p-4 rounded-sm border transition-all ${highlight ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/20 hover:border-muted-foreground/30'}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1 truncate">{label}</p>
        <p className={`text-base sm:text-lg font-medium leading-tight ${highlight ? 'text-primary' : ''}`}>{value}</p>
        {sublabel && <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 truncate">{sublabel}</p>}
      </div>
      {Icon && (
        <div className={`p-1.5 sm:p-2 rounded-sm flex-shrink-0 ${highlight ? 'bg-primary/10' : 'bg-muted'}`}>
          <Icon className={`h-3 w-3 sm:h-4 sm:w-4 ${highlight ? 'text-primary' : 'text-muted-foreground'}`} strokeWidth={1.5} />
        </div>
      )}
    </div>
  </div>
);

const SectionHeader = ({ title, subtitle }) => (
  <div className="mb-6">
    <h2 className="text-lg font-medium tracking-tight">{title}</h2>
    {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
  </div>
);

const CapitalStackBar = ({ items }) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <div className="space-y-4">
      <div className="h-6 sm:h-8 rounded-sm overflow-hidden flex">
        {items.map((item, index) => (
          <div
            key={index}
            className={`${item.color} transition-all duration-500`}
            style={{ width: `${(item.value / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm flex-shrink-0 ${item.color}`} />
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium truncate">{item.label}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{((item.value / total) * 100).toFixed(0)}%</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const UseOfFundsBar = ({ items, total, formatCurrency }) => {
  const maxValue = Math.max(...items.map(i => i.value));
  
  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{item.label}</span>
            <span className="font-medium">{formatCurrency(item.value)}</span>
          </div>
          <div className="h-2 bg-muted rounded-sm overflow-hidden">
            <div
              className="h-full bg-primary/60 transition-all duration-500"
              style={{ width: `${(item.value / maxValue) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const TimelinePhase = ({ phases }) => (
  <div className="relative">
    <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
    <div className="space-y-6">
      {phases.map((phase, index) => (
        <div key={index} className="relative pl-12">
          <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center border-2 ${
            phase.status === 'completed' ? 'bg-primary border-primary text-primary-foreground' :
            phase.status === 'current' ? 'bg-background border-primary text-primary' :
            'bg-background border-muted text-muted-foreground'
          }`}>
            {phase.status === 'completed' ? (
              <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />
            ) : (
              <span className="text-xs font-medium">{index + 1}</span>
            )}
          </div>
          <div>
            <p className={`font-medium ${phase.status === 'current' ? 'text-primary' : ''}`}>{phase.label}</p>
            <p className="text-sm text-muted-foreground">{phase.duration}</p>
            {phase.description && (
              <p className="text-xs text-muted-foreground mt-1">{phase.description}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RiskItem = ({ risk, severity }) => {
  const severityColors = {
    high: 'text-red-500',
    medium: 'text-amber-500',
    low: 'text-emerald-500'
  };
  
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <AlertCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${severityColors[severity] || 'text-muted-foreground'}`} strokeWidth={1.5} />
      <p className="text-sm text-muted-foreground leading-relaxed">{risk}</p>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const AppOpportunityDetailPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const { formatCurrency } = useI18n();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvestDialog, setShowInvestDialog] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [investConfirmations, setInvestConfirmations] = useState({
    readRisks: false,
    understandTerms: false,
    acceptDisclaimer: false
  });

  const isVerified = user?.verification_status === 'approved';

  useEffect(() => {
    fetchOpportunity();
  }, [slug]);

  const fetchOpportunity = async () => {
    try {
      const response = await api.get(`/opportunities/${slug}`);
      setOpportunity(response.data);
    } catch (error) {
      console.error('Error fetching opportunity:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/${slug}/pdf`);
      if (!response.ok) throw new Error('Error descargando PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `silver_brick_${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Abierta para Inversión' },
      soft_circle: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Soft Circle' },
      fully_funded: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Completamente Fondeada' },
      closed: { className: 'bg-muted text-muted-foreground border-muted', label: 'Cerrada' },
    };
    const style = styles[status] || styles.open;
    return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
  };

  const handleInvestClick = () => {
    setShowInvestDialog(true);
    setInvestConfirmations({ readRisks: false, understandTerms: false, acceptDisclaimer: false });
  };

  const handleConfirmInvest = () => {
    setShowInvestDialog(false);
    navigate(`/app/opportunities/${slug}/invest`);
  };

  const allConfirmationsChecked = investConfirmations.readRisks && 
    investConfirmations.understandTerms && 
    investConfirmations.acceptDisclaimer;

  if (loading) {
    return <PageSkeleton />;
  }

  if (!opportunity) {
    return (
      <div className="text-center py-16">
        <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
        <p className="text-muted-foreground">Oportunidad no encontrada</p>
        <Link to="/app/opportunities" className="text-primary underline mt-4 inline-block">
          Volver a oportunidades
        </Link>
      </div>
    );
  }

  const fundingProgress = opportunity.capital_target > 0 
    ? ((opportunity.total_committed || 0) / opportunity.capital_target) * 100 
    : 0;
  
  const canInvest = isVerified && ['open', 'soft_circle'].includes(opportunity.status);
  const hasCommitment = opportunity.user_commitment;

  // Capital stack data - transform from backend object format to array format
  const formatCapitalStack = (stack) => {
    if (!stack) return null;
    
    // If already an array, use it
    if (Array.isArray(stack)) return stack;
    
    // Transform object format to array format with colors
    const colorMap = {
      'equity': 'bg-primary',
      'senior_debt': 'bg-muted-foreground',
      'mezzanine': 'bg-primary/60',
      'preferred_equity': 'bg-primary/40',
      'junior_debt': 'bg-amber-500'
    };
    
    const labelMap = {
      'equity': 'Equity',
      'senior_debt': 'Deuda Senior',
      'mezzanine': 'Mezzanine',
      'preferred_equity': 'Equity Preferente',
      'junior_debt': 'Deuda Junior'
    };
    
    return Object.entries(stack).map(([key, value], index) => ({
      label: labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: value,
      color: colorMap[key] || ['bg-primary', 'bg-primary/60', 'bg-muted-foreground', 'bg-amber-500'][index % 4]
    }));
  };
  
  const capitalStack = formatCapitalStack(opportunity.capital_stack) || [
    { label: 'Equity Inversionistas', value: 60, color: 'bg-primary' },
    { label: 'Equity Patrocinador', value: 15, color: 'bg-primary/60' },
    { label: 'Deuda Senior', value: 25, color: 'bg-muted-foreground' },
  ];

  // Use of funds data
  const useOfFunds = opportunity.use_of_funds_breakdown ? 
    Object.entries(opportunity.use_of_funds_breakdown).map(([key, value]) => ({
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: value
    })) : [
      { label: 'Adquisición de Terreno', value: opportunity.capital_target * 0.35 },
      { label: 'Construcción', value: opportunity.capital_target * 0.45 },
      { label: 'Soft Costs', value: opportunity.capital_target * 0.08 },
      { label: 'Reservas', value: opportunity.capital_target * 0.07 },
      { label: 'Comisiones', value: opportunity.capital_target * 0.05 },
    ];

  // Timeline phases
  const timelinePhases = opportunity.timeline_phases || [
    { label: 'Adquisición', duration: '0-3 meses', status: 'current', description: 'Cierre y transferencia de propiedad' },
    { label: 'Desarrollo', duration: '3-18 meses', status: 'pending', description: 'Construcción y desarrollo del proyecto' },
    { label: 'Estabilización', duration: '18-24 meses', status: 'pending', description: 'Comercialización y ocupación' },
    { label: 'Salida', duration: '24-36 meses', status: 'pending', description: 'Venta o refinanciamiento' },
  ];

  // Risk factors - normalize from string array or object array
  const formatRiskFactors = (risks) => {
    if (!risks || !Array.isArray(risks) || risks.length === 0) return null;
    
    return risks.map((risk, index) => {
      // If already an object with text, use it
      if (typeof risk === 'object' && risk.text) {
        return risk;
      }
      // If it's a string, convert to object with auto-assigned severity
      if (typeof risk === 'string') {
        // Assign severity based on position or keywords
        let severity = 'medium';
        const lowerRisk = risk.toLowerCase();
        if (lowerRisk.includes('mercado') || lowerRisk.includes('liquidez') || lowerRisk.includes('pérdida') || lowerRisk.includes('total')) {
          severity = 'high';
        } else if (lowerRisk.includes('regulatorio') || lowerRisk.includes('dependencia') || lowerRisk.includes('tipo de cambio')) {
          severity = 'medium';
        } else {
          severity = index < 2 ? 'high' : index < 4 ? 'medium' : 'low';
        }
        return { text: risk, severity };
      }
      return { text: String(risk), severity: 'medium' };
    });
  };
  
  const riskFactors = formatRiskFactors(opportunity.risk_factors) || [
    { text: 'Riesgo de mercado: Cambios en condiciones económicas pueden afectar la demanda y precios de venta.', severity: 'high' },
    { text: 'Riesgo de construcción: Posibles retrasos, sobrecostos o problemas técnicos durante el desarrollo.', severity: 'medium' },
    { text: 'Riesgo de liquidez: La inversión es ilíquida hasta la salida del proyecto.', severity: 'high' },
    { text: 'Riesgo regulatorio: Cambios en normativas pueden impactar el proyecto.', severity: 'medium' },
    { text: 'Riesgo de ejecución: Dependencia del equipo de desarrollo y patrocinador.', severity: 'low' },
  ];

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="opportunity-detail-page">
      {/* Back Navigation */}
      <Link 
        to="/app/opportunities" 
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        Volver a oportunidades
      </Link>

      {/* ==================== HEADER SECTION ==================== */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          {getStatusBadge(opportunity.status)}
          <span className="text-sm text-muted-foreground font-mono">{opportunity.project_code}</span>
        </div>
        
        <h1 className="text-2xl md:text-3xl font-light tracking-tight">{opportunity.title}</h1>
        
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="h-4 w-4" strokeWidth={1.5} />
          <span>{[opportunity.location_city, opportunity.location_state, opportunity.location_country].filter(Boolean).join(', ') || 'Ubicación no especificada'}</span>
        </div>
      </div>

      {/* ==================== INVESTMENT SUMMARY (KEY METRICS) ==================== */}
      <Card className="card-dashboard border-primary/20">
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
            <MetricCard 
              label="Inversión Mínima"
              value={formatCurrency(opportunity.minimum_investment)}
              icon={Wallet}
              highlight
            />
            <MetricCard 
              label="Capital Objetivo"
              value={formatCurrency(opportunity.capital_target)}
              sublabel={`${fundingProgress.toFixed(0)}% comprometido`}
              icon={Target}
            />
            <MetricCard 
              label="TIR Objetivo"
              value={`${opportunity.projected_irr_base}-${opportunity.projected_irr_upside}%`}
              sublabel="Proyectado"
              icon={TrendingUp}
              highlight
            />
            <MetricCard 
              label="MOIC Objetivo"
              value={`${opportunity.projected_moic_base || 1.5}x`}
              sublabel="Múltiplo"
              icon={TrendingUp}
            />
            <MetricCard 
              label="Plazo Estimado"
              value={`${opportunity.hold_period_months || 36} meses`}
              icon={Calendar}
            />
            <MetricCard 
              label="Tipo de Proyecto"
              value={(() => {
                const typeMap = {
                  'mixed_use': 'Uso Mixto',
                  'residential': 'Residencial',
                  'industrial': 'Industrial',
                  'commercial': 'Comercial',
                  'office': 'Oficinas',
                  'retail': 'Retail',
                  'hospitality': 'Hospitalidad',
                  'land': 'Terreno'
                };
                return typeMap[opportunity.project_type] || 
                  opportunity.project_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 
                  'Desarrollo';
              })()}
              icon={Building2}
            />
          </div>
          
          {/* Funding Progress */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Progreso de Fondeo</span>
              <span className="text-sm font-medium">{formatCurrency(opportunity.total_committed || 0)} / {formatCurrency(opportunity.capital_target)}</span>
            </div>
            <Progress value={fundingProgress} className="h-2" />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">{opportunity.investor_count || 0} inversionistas</span>
              <span className="text-xs text-muted-foreground">{(100 - fundingProgress).toFixed(0)}% disponible</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ==================== MAIN CONTENT GRID ==================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT COLUMN - Main Content */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Why This Opportunity */}
          <Card className="card-dashboard">
            <CardContent className="p-6">
              <SectionHeader 
                title="Tesis de Inversión" 
                subtitle="Por qué consideramos esta oportunidad atractiva"
              />
              <div className="prose prose-sm prose-invert max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  {opportunity.thesis || opportunity.summary || 
                    `Este proyecto representa una oportunidad de inversión en el segmento de ${opportunity.project_type || 'desarrollo inmobiliario'} en ${opportunity.location_city}, una zona con fundamentos sólidos de demanda y crecimiento proyectado.`}
                </p>
                {opportunity.key_highlights && (
                  <ul className="mt-4 space-y-2">
                    {opportunity.key_highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Capital Stack */}
          <Card className="card-dashboard">
            <CardContent className="p-6">
              <SectionHeader 
                title="Estructura de Capital" 
                subtitle="Distribución del capital en el proyecto"
              />
              <CapitalStackBar items={capitalStack} />
            </CardContent>
          </Card>

          {/* Use of Funds */}
          <Card className="card-dashboard">
            <CardContent className="p-6">
              <SectionHeader 
                title="Uso de Fondos" 
                subtitle="Distribución del capital recaudado"
              />
              <UseOfFundsBar items={useOfFunds} total={opportunity.capital_target} formatCurrency={formatCurrency} />
              <p className="text-xs text-muted-foreground mt-4">
                * Distribución estimada. Sujeta a cambios según avance del proyecto.
              </p>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="card-dashboard">
            <CardContent className="p-6">
              <SectionHeader 
                title="Cronograma del Proyecto" 
                subtitle="Fases principales y duración estimada"
              />
              <TimelinePhase phases={timelinePhases} />
              <Alert className="mt-6 border-muted">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Los plazos son estimados y pueden variar según condiciones de mercado y ejecución del proyecto.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Risk Factors */}
          <Card className="card-dashboard border-amber-500/20">
            <CardContent className="p-6">
              <SectionHeader 
                title="Factores de Riesgo" 
                subtitle="Consideraciones importantes antes de invertir"
              />
              <div className="space-y-0">
                {riskFactors.map((risk, index) => (
                  <RiskItem key={index} risk={typeof risk === 'string' ? risk : risk.text} severity={risk.severity || 'medium'} />
                ))}
              </div>
              <Alert className="mt-6 border-red-500/20 bg-red-500/5">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <AlertDescription className="text-xs text-red-400">
                  <strong>ADVERTENCIA:</strong> La inversión en bienes raíces es especulativa e involucra riesgo significativo. Puede perder parte o la totalidad de su inversión. No invierta capital que no pueda permitirse perder.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Financial Details */}
          <Card className="card-dashboard">
            <CardContent className="p-6">
              <SectionHeader 
                title="Estructura de Comisiones" 
                subtitle="Costos asociados a la inversión"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {opportunity.fees_management && (
                  <div className="p-4 bg-muted/20 rounded-sm">
                    <p className="text-xs text-muted-foreground mb-1">Comisión de Administración</p>
                    <p className="text-lg font-medium">{opportunity.fees_management}%</p>
                    <p className="text-xs text-muted-foreground">anual sobre capital invertido</p>
                  </div>
                )}
                {opportunity.fees_performance && (
                  <div className="p-4 bg-muted/20 rounded-sm">
                    <p className="text-xs text-muted-foreground mb-1">Comisión de Desempeño</p>
                    <p className="text-lg font-medium">{opportunity.fees_performance}%</p>
                    <p className="text-xs text-muted-foreground">sobre utilidades netas</p>
                  </div>
                )}
                {opportunity.fees_acquisition && (
                  <div className="p-4 bg-muted/20 rounded-sm">
                    <p className="text-xs text-muted-foreground mb-1">Comisión de Adquisición</p>
                    <p className="text-lg font-medium">{opportunity.fees_acquisition}%</p>
                    <p className="text-xs text-muted-foreground">única al cierre</p>
                  </div>
                )}
              </div>
              {opportunity.fees_summary && (
                <p className="text-sm text-muted-foreground mt-4">{opportunity.fees_summary}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT COLUMN - Actions & Documents */}
        <div className="space-y-6">
          
          {/* Investment Action Card */}
          <Card className="card-dashboard sticky top-6">
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="font-medium mb-2">Comprometer Capital</h3>
                <p className="text-sm text-muted-foreground">
                  {isVerified 
                    ? `Inversión mínima: ${formatCurrency(opportunity.minimum_investment)}`
                    : 'Complete su verificación para invertir'}
                </p>
              </div>

              {/* User's existing commitment */}
              {hasCommitment && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-sm">
                  <p className="text-sm font-medium text-primary mb-1">Compromiso Registrado</p>
                  <p className="text-xl font-light">{formatCurrency(hasCommitment.amount)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Estado: {hasCommitment.status === 'pending' ? 'Pendiente de revisión' : 
                            hasCommitment.status === 'approved' ? 'Aprobado' :
                            hasCommitment.status === 'confirmed' ? 'Confirmado' : hasCommitment.status}
                  </p>
                </div>
              )}

              {/* CTA Button */}
              {!isVerified ? (
                <div className="space-y-3">
                  <Alert className="border-amber-500/20 bg-amber-500/5">
                    <Lock className="h-4 w-4 text-amber-500" />
                    <AlertDescription className="text-sm text-amber-500">
                      Debe completar su verificación KYC para poder invertir en este proyecto.
                    </AlertDescription>
                  </Alert>
                  <Link to="/app/profile" className="block">
                    <Button className="w-full btn-outline" data-testid="complete-verification-btn">
                      Completar Verificación
                      <ChevronRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                    </Button>
                  </Link>
                </div>
              ) : !['open', 'soft_circle'].includes(opportunity.status) ? (
                <Button disabled className="w-full">
                  Inversión No Disponible
                </Button>
              ) : hasCommitment ? (
                <Link to="/app/investments" className="block">
                  <Button className="w-full btn-outline">
                    Ver Mis Inversiones
                  </Button>
                </Link>
              ) : (
                <Button 
                  className="w-full btn-primary h-12 text-base" 
                  data-testid="invest-button"
                  onClick={handleInvestClick}
                >
                  <Wallet className="h-5 w-5 mr-2" strokeWidth={1.5} />
                  Invertir en este Proyecto
                </Button>
              )}

              <Button 
                variant="outline" 
                className="w-full h-10" 
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} />
                    Generando PDF...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Descargar Información Completa
                  </>
                )}
              </Button>

              <Separator />

              {/* Key Terms Summary */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Términos Clave</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Inversión mínima</span>
                    <span className="font-medium">{formatCurrency(opportunity.minimum_investment)}</span>
                  </div>
                  {opportunity.maximum_investment && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Inversión máxima</span>
                      <span className="font-medium">{formatCurrency(opportunity.maximum_investment)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Plazo objetivo</span>
                    <span className="font-medium">{opportunity.hold_period_months || 36} meses</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Nivel de riesgo</span>
                    <span className={`font-medium ${
                      opportunity.risk_level === 'high' ? 'text-red-500' :
                      opportunity.risk_level === 'low' ? 'text-emerald-500' : 'text-amber-500'
                    }`}>
                      {opportunity.risk_level === 'high' ? 'Alto' :
                       opportunity.risk_level === 'low' ? 'Bajo' : 'Medio'}
                    </span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Document Access */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Documentos del Proyecto</h4>
                {!isVerified ? (
                  <div className="p-4 bg-muted/30 rounded-sm text-center">
                    <Lock className="h-6 w-6 mx-auto mb-2 text-muted-foreground" strokeWidth={1.5} />
                    <p className="text-sm text-muted-foreground">
                      Complete su verificación para acceder a los documentos confidenciales del proyecto.
                    </p>
                  </div>
                ) : opportunity.documents && opportunity.documents.length > 0 ? (
                  <div className="space-y-2">
                    {opportunity.documents.slice(0, 3).map((doc) => (
                      <div 
                        key={doc.document_id}
                        className="flex items-center justify-between p-3 border border-border rounded-sm hover:border-primary/30 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                          <span className="text-sm truncate">{doc.description || doc.original_filename}</span>
                        </div>
                        <Download className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      </div>
                    ))}
                    {opportunity.documents.length > 3 && (
                      <Button variant="ghost" className="w-full text-sm">
                        Ver todos los documentos ({opportunity.documents.length})
                        <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-muted/30 rounded-sm text-center">
                    <FileText className="h-6 w-6 mx-auto mb-2 text-muted-foreground opacity-50" strokeWidth={1.5} />
                    <p className="text-sm text-muted-foreground">
                      Los documentos estarán disponibles próximamente.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sponsor Info */}
          {(opportunity.sponsor_name || opportunity.sponsor_info) && (
            <Card className="card-dashboard">
              <CardContent className="p-6">
                <h4 className="text-sm font-medium mb-3">Patrocinador del Proyecto</h4>
                {opportunity.sponsor_name && (
                  <p className="font-medium mb-2">{opportunity.sponsor_name}</p>
                )}
                {opportunity.sponsor_info && (
                  <p className="text-sm text-muted-foreground">{opportunity.sponsor_info}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Help Section */}
          <Card className="card-dashboard border-muted">
            <CardContent className="p-6">
              <h4 className="text-sm font-medium mb-3">¿Necesita Ayuda?</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Nuestro equipo está disponible para responder sus preguntas sobre esta oportunidad.
              </p>
              <Link to="/contact">
                <Button variant="outline" className="w-full">
                  Contactar al Equipo
                  <ExternalLink className="h-4 w-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ==================== FOOTER DISCLAIMER ==================== */}
      <Card className="border-muted bg-muted/20">
        <CardContent className="p-4 sm:p-6">
          <h4 className="text-sm font-medium mb-3">Aviso Legal</h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Esta información es presentada por Silver Brick Development como fondo de inversión inmobiliaria de alto nivel institucional. Contamos con estructuras legales y financieras sólidas diseñadas para proteger su inversión. Nuestro equipo está a su disposición para resolver cualquier duda y acompañarlo en cada paso del proceso.
          </p>
        </CardContent>
      </Card>

      {/* ==================== INVESTMENT CONFIRMATION DIALOG ==================== */}
      <Dialog open={showInvestDialog} onOpenChange={setShowInvestDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Confirmar Intención de Inversión</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground pt-2">
              Antes de continuar con el proceso de inversión en <span className="font-medium text-foreground">{opportunity?.title}</span>, 
              por favor confirme que ha leído y entiende lo siguiente:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-start space-x-3">
              <Checkbox 
                id="readRisks"
                checked={investConfirmations.readRisks}
                onCheckedChange={(checked) => setInvestConfirmations(prev => ({...prev, readRisks: checked}))}
              />
              <label htmlFor="readRisks" className="text-sm leading-relaxed cursor-pointer">
                He leído y entendido los <span className="font-medium text-amber-500">factores de riesgo</span> asociados a esta inversión, 
                sin embargo, nuestras estructuras están diseñadas para mitigar estos factores al máximo.
              </label>
            </div>
            
            <div className="flex items-start space-x-3">
              <Checkbox 
                id="understandTerms"
                checked={investConfirmations.understandTerms}
                onCheckedChange={(checked) => setInvestConfirmations(prev => ({...prev, understandTerms: checked}))}
              />
              <label htmlFor="understandTerms" className="text-sm leading-relaxed cursor-pointer">
                Entiendo los <span className="font-medium">términos y estructura</span> de la inversión, incluyendo el plazo estimado 
                de {opportunity?.hold_period_months || 36} meses y la inversión mínima de {formatCurrency(opportunity?.minimum_investment)}.
              </label>
            </div>
            
            <div className="flex items-start space-x-3">
              <Checkbox 
                id="acceptDisclaimer"
                checked={investConfirmations.acceptDisclaimer}
                onCheckedChange={(checked) => setInvestConfirmations(prev => ({...prev, acceptDisclaimer: checked}))}
              />
              <label htmlFor="acceptDisclaimer" className="text-sm leading-relaxed cursor-pointer">
                Reconozco que esta plataforma no proporciona <span className="font-medium">asesoramiento de inversión, fiscal o legal</span>, 
                y que debo consultar con mis propios asesores antes de tomar decisiones de inversión.
              </label>
            </div>
          </div>

          <Alert className="border-amber-500/20 bg-amber-500/5">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-xs text-amber-600">
              Al continuar, acepta que ha revisado toda la información disponible sobre esta oportunidad y 
              que comprende los riesgos involucrados.
            </AlertDescription>
          </Alert>
          
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setShowInvestDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              disabled={!allConfirmationsChecked}
              onClick={handleConfirmInvest}
            >
              <Wallet className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Continuar a Inversión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppOpportunityDetailPage;
